import { FC, JSX } from "react";
import { SidebarGroup, SidebarGroupLabel, SidebarGroupAction, SidebarGroupContent, SidebarMenu } from "@capacit-com/lara-law-shared-ui";
import {  } from "@capacit-com/lara-law-shared-ui";
import { ChevronUpIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SidebarMenuList from "./SidebarMenuList";
import type { NavigationItemType } from "@/constants/routes/NavItems";
import useSidebarStore from "@/stores/sidebar.store";

interface Props {
  title: string;
  open: boolean;
  items: NavigationItemType[];
  collapsed: boolean;
  toggle: () => void;
  currentPath: string;
  groupedItems?: Record<string, NavigationItemType[]>; // for admin subgroups
  collapsibleSubGroups?: boolean;
  iconResolver?: (groupName: string) => JSX.Element | null;
  indentSubItems?: boolean;
  sliceNumber?: number;
  customIsActive?: boolean | undefined;
  noSeperator?: boolean;
}

const SidebarGroupSection: FC<Props> = ({
  title,
  open,
  items,
  groupedItems,
  collapsed,
  toggle,
  currentPath,
  collapsibleSubGroups = false,
  iconResolver,
  indentSubItems = false,
  sliceNumber = 2,
  customIsActive,
  noSeperator = false,
}) => {
  const { collapsedSubGroups, toggleSubGroup } = useSidebarStore();

  return (
    <SidebarGroup className={`px-2 py-0 ${!open ? "px-1.5" : ""}`}>
      {open && (
        <SidebarGroupLabel
          className="cursor-pointer group/label hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex items-center justify-between"
          onClick={toggle}
        >
          <span>{title}</span>
          <SidebarGroupAction className="!relative !top-0 !right-0 flex items-center justify-center group-hover/label:text-sidebar-accent-foreground">
            <ChevronUpIcon
              className={`transition-transform duration-200 ${
                collapsed ? "rotate-[-180deg]" : "rotate-0"
              }`}
            />
          </SidebarGroupAction>
        </SidebarGroupLabel>
      )}

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            key="group-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuList
                  items={items}
                  currentPath={currentPath}
                  open={open}
                  noSeperator={noSeperator}
                  customIsActive={customIsActive}
                />

                {groupedItems &&
                  Object.entries(groupedItems).map(([groupName, subItems]) => {
                    const isCollapsed = collapsedSubGroups[groupName];

                    return (
                      <div key={groupName}>
                        {collapsibleSubGroups && open && (
                          <div
                            onClick={() => toggleSubGroup(groupName)}
                            className="cursor-pointer group/subgroup hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-8 flex items-center justify-between px-2 rounded-md text-sm"
                          >
                            <span className="flex items-center gap-2">
                              {iconResolver?.(groupName)}
                              <p className="text-sidebar-foreground/70 group-hover/subgroup:text-sidebar-accent-foreground">
                                {groupName}
                              </p>
                            </span>
                            <ChevronUpIcon
                              className={`transition-transform duration-200 mr-[2px] size-4 group-hover/subgroup:text-sidebar-accent-foreground ${
                                isCollapsed ? "rotate-[-180deg]" : "rotate-0"
                              }`}
                            />
                          </div>
                        )}

                        <AnimatePresence initial={false}>
                          {(!isCollapsed || !open) && (
                            <motion.div
                              key={`subgroup-${groupName}`}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <SidebarMenuList
                                items={subItems}
                                currentPath={currentPath}
                                open={open}
                                indent={indentSubItems}
                                sliceNumber={sliceNumber}
                                customIsActive={customIsActive}
                                noSeperator={noSeperator}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
              </SidebarMenu>
            </SidebarGroupContent>
          </motion.div>
        )}
      </AnimatePresence>
    </SidebarGroup>
  );
};

export default SidebarGroupSection;
